import { useEffect, useState } from 'react';
import { usesPhoneLayout } from '@player/domain';

/**
 * Se disegnare con la disposizione da telefono, **adesso**.
 *
 * ## Perché non si decide una volta all'avvio
 *
 * Perché la larghezza cambia mentre l'app è aperta, e in due modi che capitano davvero: si **ruota**
 * il telefono, e si ridimensiona la finestra di un browser. Letta una volta sola, un telefono girato
 * in orizzontale resterebbe con la barra delle schede in basso su uno schermo largo 844 px — e una
 * finestra allargata resterebbe con l'interfaccia da telefono su un monitor.
 *
 * La regola però **non è qui**: sta in `usesPhoneLayout`, nel dominio, dove si prova senza montare
 * niente. Qui c'è solo il mestiere di accorgersi che la misura è cambiata.
 *
 * ## `visualViewport` quando c'è
 *
 * Su un telefono la tastiera di sistema che si apre non cambia `innerWidth`, ma `resize` non scatta
 * nemmeno — mentre `visualViewport` riporta la finestra davvero visibile. Si ascoltano entrambi:
 * `resize` della finestra copre la rotazione e il ridimensionamento, `visualViewport` copre il resto
 * e non esiste su tutti i motori che questa app deve reggere.
 *
 * ## Ruotare non cambia disposizione
 *
 * E non è un dettaglio: la regola guarda il **lato corto**, quindi girando il telefono la risposta
 * non cambia, l'albero non si rimonta e il canale non riparte. Prima bastava ruotare per far
 * ricominciare la riproduzione da capo.
 */
export function usePhoneLayout(): boolean {
  const [phone, setPhone] = useState(() => decide());

  useEffect(() => {
    const aggiorna = () => setPhone(decide());

    /*
      Si misura **anche subito**, e non solo agli eventi.

      Fra il primo disegno e questo effetto la finestra può già essere cambiata — succede
      all'apertura, quando il motore assesta la pagina — e senza questa riga quel cambiamento non
      arriverebbe fino al prossimo evento, cioè potenzialmente mai.
    */
    aggiorna();

    window.addEventListener('resize', aggiorna);
    window.visualViewport?.addEventListener('resize', aggiorna);
    return () => {
      window.removeEventListener('resize', aggiorna);
      window.visualViewport?.removeEventListener('resize', aggiorna);
    };
  }, []);

  return phone;
}

/**
 * La misura, presa dal documento.
 *
 * Fuori da un browser — una prova di dominio, un disegno dal server — risponde `false`: la
 * disposizione da televisore è quella che funziona anche con un puntatore, mentre quella da telefono
 * su uno schermo largo è inutilizzabile col telecomando.
 */
function decide(): boolean {
  if (typeof window === 'undefined') return false;
  return usesPhoneLayout({
    userAgent: window.navigator?.userAgent ?? '',
    viewportWidth: window.visualViewport?.width ?? window.innerWidth,
    viewportHeight: window.visualViewport?.height ?? window.innerHeight,
  });
}
